import { Button, Checkbox, Modal, Text } from "rizzui";
import { useState } from "react";
import dayjs from "dayjs";
import { PiPaperPlaneTiltBold } from "react-icons/pi";
import { ICreatePushNotificationDTO } from "@/utilities/types/PushNotification";

type ScheduleMode = "immediate" | "scheduled";

export interface ConfirmBlastModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  data: ICreatePushNotificationDTO;
  scheduleMode: ScheduleMode;
  scheduledAt: Date | null;
  isLoading?: boolean;
}

export default function ConfirmBlastModal({
  isOpen,
  onClose,
  onConfirm,
  data,
  scheduleMode,
  scheduledAt,
  isLoading,
}: ConfirmBlastModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const showAck = scheduleMode === "immediate";

  const canSubmit = (!showAck || confirmed) && !isLoading;
  const isSchedule = scheduleMode === "scheduled";

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } finally {
      setConfirmed(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    setConfirmed(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <div className="p-6">
        <h2 className="text-xl font-bold">
          {isSchedule ? "Schedule Push Notification" : "Confirm Push Notification Blast"}
        </h2>
        <Text className="mt-1 text-sm text-gray-500">
          {isSchedule
            ? "Review the details and confirm scheduling."
            : "Please review the details before sending."}
        </Text>

        {/* Preview section */}
        <div className="mt-5 space-y-4 rounded-md border border-muted bg-gray-50/50 p-4">
          <Row label="Title" value={data.title} />
          <Row label="Message" value={data.body} />
          {data.imageUrl ? (
            <div>
              <div className="mb-2 text-xs uppercase tracking-wider text-gray-500">
                Image
              </div>
              <img
                src={data.imageUrl}
                alt="Notification banner preview"
                className="max-h-32 rounded-md border border-muted object-cover"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          ) : null}
          {data.deepLink ? <Row label="Deep link" value={data.deepLink} /> : null}

          {isSchedule && scheduledAt ? (
            <Row
              label="Schedule"
              value={dayjs(scheduledAt).format("MMM DD, YYYY HH:mm")}
            />
          ) : (
            <Row label="Schedule" value="Send immediately" />
          )}
        </div>

        {showAck && (
          <label className="mt-4 flex items-start gap-2 text-sm text-gray-700">
            <Checkbox
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              I understand this notification will be sent immediately and cannot
              be recalled.
            </span>
          </label>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            isLoading={isLoading}
            disabled={!canSubmit}
          >
            {isSchedule ? (
              "Schedule"
            ) : (
              <>
                <PiPaperPlaneTiltBold className="me-1.5 h-4 w-4" />
                Send Now
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Row({
  label,
  value,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="w-28 shrink-0 text-xs uppercase tracking-wider text-gray-500">
        {label}
      </div>
      <div className="flex-1 text-right text-sm text-gray-800">{value}</div>
    </div>
  );
}
